import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, AlertCircle, Info, ChevronRight, X } from 'lucide-react';
import { fetchAvvisi, dismissAvviso } from '../../lib/api';

/**
 * Pannello avvisi non invasivi.
 *
 * Rendering modes:
 *  - variant="sidebar": pill compatta con counter; tooltip sui dettagli
 *  - variant="dashboard": elenco verticale di card cliccabili
 *
 * Auto-refresh ogni 5 minuti. Riascolta `qp:quick-add-created` per aggiornare
 * subito dopo nuove MIT/azioni.
 *
 * Dismiss: gli avvisi con `dismissable=true` mostrano una "X" che chiude
 * l'avviso per quel (kind, periodo). Lo stato persiste in DB e fra refresh.
 */
const ICON = {
  danger: <AlertCircle size={13} strokeWidth={1.5} className="text-accent" />,
  warning: <AlertTriangle size={13} strokeWidth={1.5} className="text-[#D97706]" />,
  info: <Info size={13} strokeWidth={1.5} className="text-ink" />,
};

const BG = {
  danger: 'bg-risk/10 border-accent',
  warning: 'bg-[#FFFBEB] border-[#D97706]',
  info: 'bg-linesoft border-line',
};

export default function AvvisiPanel({ variant = 'dashboard' }) {
  const [avvisi, setAvvisi] = useState([]);
  const [dismissing, setDismissing] = useState({}); // kind+periodo -> bool

  const load = () => {
    fetchAvvisi()
      .then((d) => setAvvisi(d.avvisi || []))
      .catch(() => { /* silent: niente avvisi se backend non risponde */ });
  };

  useEffect(() => {
    let alive = true;
    const safeLoad = () => {
      fetchAvvisi()
        .then((d) => { if (alive) setAvvisi(d.avvisi || []); })
        .catch(() => {});
    };
    safeLoad();
    const id = setInterval(safeLoad, 5 * 60 * 1000);
    window.addEventListener('qp:quick-add-created', safeLoad);
    return () => { alive = false; clearInterval(id); window.removeEventListener('qp:quick-add-created', safeLoad); };
  }, []);

  const handleDismiss = (e, a) => {
    // Evita di seguire il Link parent
    e.preventDefault();
    e.stopPropagation();
    const key = `${a.kind}::${a.periodo || ''}`;
    if (dismissing[key]) return;
    setDismissing((s) => ({ ...s, [key]: true }));
    // Rimozione ottimistica
    setAvvisi((list) => list.filter((x) => !(x.kind === a.kind && (x.periodo || '') === (a.periodo || ''))));
    dismissAvviso(a.kind, a.periodo || '')
      .catch(() => {
        // Ripristina in caso di errore
        load();
      })
      .finally(() => {
        setDismissing((s) => { const c = { ...s }; delete c[key]; return c; });
      });
  };

  if (avvisi.length === 0) return null;

  if (variant === 'sidebar') {
    return (
      <div className="px-2 mt-3" data-testid="avvisi-sidebar">
        <div className="px-2 mb-1 font-mono text-[10px] uppercase tracking-[0.22em] text-mute">
          Avvisi · {avvisi.length}
        </div>
        <ul className="space-y-0.5">
          {avvisi.map((a, i) => (
            <li key={`${a.kind}-${a.periodo || ''}-${a.action_route || i}`} className="relative group/avviso">
              <Link
                to={a.action_route}
                className={`flex items-center gap-2 pl-3 pr-7 py-1.5 font-body text-[12px] border-l-2 transition-colors hover:bg-linesoft ${BG[a.severity] || BG.info}`}
                data-testid={`avviso-sidebar-${a.kind}`}
                title={a.desc}
              >
                <span className="shrink-0">{ICON[a.severity] || ICON.info}</span>
                <span className="truncate flex-1">{a.title}</span>
              </Link>
              {a.dismissable && (
                <button
                  type="button"
                  onClick={(e) => handleDismiss(e, a)}
                  className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded opacity-0 group-hover/avviso:opacity-100 hover:bg-black/10 transition-opacity"
                  title="Chiudi avviso"
                  aria-label="Chiudi avviso"
                  data-testid={`avviso-dismiss-sidebar-${a.kind}`}
                >
                  <X size={11} strokeWidth={1.8} className="text-soft" />
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  // Dashboard variant — elenco verticale di card
  return (
    <div className="mb-8 space-y-2" data-testid="avvisi-dashboard">
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-mute mb-2">
        Avvisi · {avvisi.length}
      </div>
      {avvisi.map((a, i) => (
        <div
          key={`${a.kind}-${a.periodo || ''}-${a.action_route || i}`}
          className={`relative flex items-stretch border-l-4 ${BG[a.severity] || BG.info} hover:opacity-90 transition-opacity group`}
        >
          <Link
            to={a.action_route}
            className="flex items-center gap-3 px-4 py-3 flex-1 min-w-0"
            data-testid={`avviso-dashboard-${a.kind}`}
          >
            <span className="shrink-0">{ICON[a.severity] || ICON.info}</span>
            <div className="flex-1 min-w-0">
              <div className="font-display text-sm tracking-tight">{a.title}</div>
              {a.desc && <div className="font-body text-xs text-soft mt-0.5">{a.desc}</div>}
            </div>
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-soft shrink-0 inline-flex items-center gap-1 group-hover:text-ink">
              {a.action_label} <ChevronRight size={12} strokeWidth={1.5} />
            </span>
          </Link>
          {a.dismissable && (
            <button
              type="button"
              onClick={(e) => handleDismiss(e, a)}
              className="px-3 flex items-center justify-center border-l border-black/5 hover:bg-black/5 transition-colors"
              title="Chiudi avviso"
              aria-label="Chiudi avviso"
              data-testid={`avviso-dismiss-dashboard-${a.kind}`}
            >
              <X size={14} strokeWidth={1.8} className="text-soft" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
